import LegalLayout from './LegalLayout.jsx';

/**
 * User Data Deletion instructions — the third URL Meta App Review wants.
 *
 * Meta accepts EITHER:
 *   - A "data deletion callback" endpoint they POST a signed request to
 *     and we return a confirmation_code + status URL, OR
 *   - A public instructions page describing how a user can request deletion.
 *
 * We chose the instructions-page path because it's vastly simpler and
 * doesn't require an additional signed-request handler in the api. The
 * downside is the user has to email — fine for a private beta.
 */
export default function DataDeletion() {
  return (
    <LegalLayout title="User Data Deletion" lastUpdated="June 1, 2026">
      <section>
        <h2>How to request deletion of your data</h2>
        <p>
          You can request deletion of all personal data Markaii holds about you at
          any time. We'll delete it from production systems within 30 days and from
          encrypted backups within an additional 30 days.
        </p>
      </section>

      <section>
        <h2>If you have a Markaii account</h2>
        <ol>
          <li>Sign in at <a href="/login">markaii.com/login</a></li>
          <li>Go to <a href="/account">Account → Profile</a></li>
          <li>Click "Delete account" at the bottom of the page<br/>
            <span className="muted small">(Or email <a href="mailto:privacy@markaii.com">privacy@markaii.com</a> from your account email if the self-serve button is not yet available.)</span></li>
          <li>Confirm — your account, your projects, your generated content, your connected social account tokens, and your audit log are all deleted within 30 days.</li>
        </ol>
      </section>

      <section>
        <h2>If you connected Markaii to your Facebook / Instagram account and want only that link severed</h2>
        <p>
          You don't need to delete your whole Markaii account just to disconnect a
          social platform.
        </p>
        <ol>
          <li>Sign in at <a href="/login">markaii.com</a></li>
          <li>Open the project the account is connected to</li>
          <li>Scroll to "Connections", click <b>Disconnect</b> on the relevant account</li>
        </ol>
        <p>
          You can also revoke access directly from Facebook:{' '}
          <a href="https://www.facebook.com/settings?tab=business_tools" target="_blank" rel="noreferrer">
            facebook.com/settings → Business Integrations
          </a>{' '}
          → find "Markaii Marketing OS" → Remove. The access token becomes invalid
          immediately and we will detect the revocation on the next stats poll
          (within 24 hours) at which point we mark the account as revoked in our
          database and stop storing the token.
        </p>
      </section>

      <section>
        <h2>If you don't have a Markaii account (but think we have your data)</h2>
        <p>
          This can happen if you were a team member someone invited and never
          accepted, or if you're listed as a contact on a brand someone else manages.
          Email <a href="mailto:privacy@markaii.com">privacy@markaii.com</a> from the
          address in question and we'll confirm what we have and delete it.
        </p>
      </section>

      <section>
        <h2>What happens after deletion</h2>
        <ul>
          <li>Production database: deleted within 30 days of your request</li>
          <li>Encrypted backups: aged out within an additional 30 days</li>
          <li>Connected-platform access tokens: revoked and purged immediately</li>
          <li>Generated images stored at Backblaze B2: deleted within 30 days</li>
          <li>Audit log entries: deletion request itself is logged for compliance purposes; that single entry retains your email for 7 years per record-keeping requirements, after which it too is purged.</li>
        </ul>
      </section>

      <section>
        <h2>Need help?</h2>
        <p>
          Email <a href="mailto:privacy@markaii.com">privacy@markaii.com</a>. We
          respond within 30 days as required by GDPR Art. 12(3) — typically much
          faster.
        </p>
      </section>
    </LegalLayout>
  );
}
